import React, { useState, useEffect } from 'react';
import {
  Users, Search, Filter, CheckCircle, XCircle, AlertCircle,
  Clock, Eye, Phone, Mail, Building2, MapPin, Car, Calendar,
  ChevronLeft, ChevronRight, RefreshCw, Shield, ShieldOff,
  ShieldCheck, ShieldAlert, MoreVertical, FileText, ExternalLink,
  FileCheck, FileX, Share2, Check
} from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import RefreshButton from '../../components/ui/RefreshButton';
import Input from '../../components/ui/Input';
import Card, { CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Toast from '../../components/ui/Toast';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { API_ENDPOINTS } from '../../config/api';

// How long an admin-granted plan lasts. `months: null` = no expiry date is stored,
// so the plan never lapses back to Free (see backend/src/config/plans.js).
const DURATIONS = [
  { value: '1m', label: '1 month', months: 1 },
  { value: '3m', label: '3 months', months: 3 },
  { value: '6m', label: '6 months', months: 6 },
  { value: '1y', label: '1 year', months: 12 },
  { value: 'never', label: 'Never expires', months: null }
];

// Mirrors backend/src/config/plans.js — shown as the placeholder when no override is set.
const PLAN_DEFAULT_VEHICLES = { Free: 3, Basic: 10, Professional: 30, Enterprise: '∞' };

const expiryFor = (plan, duration) => {
  if (plan === 'Free') return null; // Free never expires
  const d = DURATIONS.find((x) => x.value === duration);
  if (!d || !d.months) return null;
  const date = new Date();
  date.setMonth(date.getMonth() + d.months);
  return date;
};

const fmtDate = (v) => (v
  ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '');

const isExpired = (sub) => !!(sub?.plan && sub.plan !== 'Free' && sub.expiresAt && new Date(sub.expiresAt) < new Date());

const OwnersList = () => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    all: 0,
    pending: 0,
    verified: 0,
    rejected: 0,
    suspended: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [pendingBiz, setPendingBiz] = useState(null); // { ownerId, ownerName, businessType }
  const [pendingPlan, setPendingPlan] = useState(null); // { ownerId, ownerName, plan, duration, expiresAt }
  const [planDraft, setPlanDraft] = useState({}); // ownerId -> { plan, duration } before Apply
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  // Build the shareable public link for an agency (rich-preview /share URL)
  const apiOrigin = (process.env.REACT_APP_API_URL || `${window.location.origin}/api`).replace(/\/api\/?$/, '');
  const ownerShareUrl = (ownerId) => `${apiOrigin}/share/travels/${ownerId}`;

  const shareOwner = async (owner) => {
    const url = ownerShareUrl(owner._id);
    const title = owner.travelsName || owner.name || 'Travels';
    if (navigator.share) {
      navigator.share({ title, url }).catch(() => {});
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    }
    setCopiedId(owner._id);
    setTimeout(() => setCopiedId(null), 2000);
  };
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docAction, setDocAction] = useState('');
  const [docNotes, setDocNotes] = useState('');
  const [docLoading, setDocLoading] = useState(false);

  const fetchOwners = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        status: filters.status,
        ...(filters.search && { search: filters.search })
      });

      const response = await fetch(`${API_ENDPOINTS.USERS}/admin/owners?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setOwners(data.owners);
        setPagination(data.pagination);
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching owners:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOwners();
  }, [pagination.page, filters.status]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchOwners();
  };

  const handleVerify = async (ownerId) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.USERS}/admin/owners/${ownerId}/verify`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: actionReason })
      });

      const data = await response.json();
      if (data.success) {
        fetchOwners();
        setShowActionModal(false);
        setActionReason('');
      }
    } catch (error) {
      console.error('Error verifying owner:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetBusinessType = async (ownerId, businessType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.USERS}/admin/owners/${ownerId}/business-type`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessType })
      });
      const data = await response.json();
      if (data.success) {
        setToast({ isOpen: true, title: 'Updated', message: `Business type set to ${businessType}.`, type: 'success' });
        fetchOwners();
      } else {
        setToast({ isOpen: true, title: 'Error', message: data.message || 'Failed to update', type: 'error' });
      }
    } catch (error) {
      console.error('Error setting business type:', error);
      setToast({ isOpen: true, title: 'Error', message: 'Failed to update business type', type: 'error' });
    }
  };

  // Business type drives which features the owner sees (rental vs Buy/Sell), and it
  // decides whether a "drivers" limit is even meaningful on their subscription.
  const renderBusinessType = (owner) => (
    <select
      value={owner.businessType || 'both'}
      onChange={(e) => setPendingBiz({ ownerId: owner._id, ownerName: owner.name, businessType: e.target.value })}
      className="border border-gray-200 rounded-lg px-2 py-1 text-xs font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    >
      <option value="rental">Renting</option>
      <option value="sale">Selling</option>
      <option value="both">Both</option>
    </select>
  );

  // Plan + duration + Apply, plus the saved expiry. Shared by the mobile cards and
  // the desktop table so the two can't drift apart.
  const renderPlanControl = (owner) => {
    const sub = owner.subscription || {};
    const draft = planDraft[owner._id] || {};
    const plan = draft.plan ?? (sub.plan || 'Free');
    const duration = draft.duration ?? '1y'; // safer default than "never"
    const savedLimit = sub.vehicleLimit ?? '';
    const vehicleLimit = draft.vehicleLimit ?? savedLimit; // '' = use the plan's default
    const expired = isExpired(sub);
    const setDraft = (patch) => setPlanDraft((d) => ({ ...d, [owner._id]: { plan, duration, vehicleLimit, ...patch } }));
    const dirty = plan !== (sub.plan || 'Free')
      || draft.duration !== undefined
      || (draft.vehicleLimit !== undefined && String(draft.vehicleLimit) !== String(savedLimit));

    return (
      <div className="space-y-1">
        <div className="flex items-center gap-1.5">
          <select
            value={plan}
            onChange={(e) => setDraft({ plan: e.target.value })}
            className="border border-gray-200 rounded-lg px-2 py-1 text-xs font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="Free">Free</option>
            <option value="Basic">Basic</option>
            <option value="Professional">Professional</option>
            <option value="Enterprise">Enterprise</option>
          </select>
          {plan !== 'Free' && (
            <>
              <select
                value={duration}
                onChange={(e) => setDraft({ duration: e.target.value })}
                className="border border-gray-200 rounded-lg px-2 py-1 text-xs font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {DURATIONS.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
              <input
                type="number"
                min="0"
                value={vehicleLimit}
                onChange={(e) => setDraft({ vehicleLimit: e.target.value })}
                placeholder={PLAN_DEFAULT_VEHICLES[plan]}
                title={`Vehicles allowed. Leave blank for the ${plan} default (${PLAN_DEFAULT_VEHICLES[plan]}).`}
                className="w-20 border border-gray-200 rounded-lg px-2 py-1 text-xs font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </>
          )}
          <button
            type="button"
            disabled={!dirty}
            onClick={() => setPendingPlan({
              ownerId: owner._id,
              ownerName: owner.name,
              plan,
              duration,
              vehicleLimit: plan === 'Free' ? '' : vehicleLimit,
              expiresAt: expiryFor(plan, duration)
            })}
            className="px-2.5 py-1 rounded-lg text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>

        {/* Saved state, straight from the subscription */}
        <div className="text-[11px] whitespace-nowrap">
          {(sub.plan || 'Free') === 'Free' ? (
            <span className="text-gray-400">Free — 3 vehicles, no expiry</span>
          ) : expired ? (
            <span className="font-semibold text-red-600">Expired {fmtDate(sub.expiresAt)} — on Free limits</span>
          ) : (
            <span className="text-gray-500">
              {savedLimit !== '' ? `${savedLimit} vehicles` : `${PLAN_DEFAULT_VEHICLES[sub.plan]} vehicles`}
              {' · '}
              {sub.expiresAt
                ? `expires ${fmtDate(sub.expiresAt)}`
                : <span className="text-amber-600 font-medium">never expires</span>}
            </span>
          )}
        </div>
      </div>
    );
  };

  const handleSetPlan = async (ownerId, plan, expiresAt, vehicleLimit) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.USERS}/admin/owners/${ownerId}/subscription`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan,
          expiresAt: expiresAt ? expiresAt.toISOString() : null,
          vehicleLimit: vehicleLimit === '' ? null : vehicleLimit
        })
      });
      const data = await response.json();
      if (data.success) {
        setToast({
          isOpen: true,
          title: 'Updated',
          message: expiresAt ? `Plan set to ${plan}, expires ${fmtDate(expiresAt)}.` : `Plan set to ${plan} (no expiry).`,
          type: 'success'
        });
        setPlanDraft((d) => { const next = { ...d }; delete next[ownerId]; return next; });
        fetchOwners();
      } else {
        setToast({ isOpen: true, title: 'Error', message: data.message || 'Failed to update', type: 'error' });
      }
    } catch (error) {
      console.error('Error setting plan:', error);
      setToast({ isOpen: true, title: 'Error', message: 'Failed to update plan', type: 'error' });
    }
  };

  const handleReject = async (ownerId) => {
    if (!actionReason.trim()) {
      setToast({ isOpen: true, title: 'Heads up', message: 'Please provide a reason for rejection', type: 'warning' });
      return;
    }
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.USERS}/admin/owners/${ownerId}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: actionReason })
      });

      const data = await response.json();
      if (data.success) {
        fetchOwners();
        setShowActionModal(false);
        setActionReason('');
      }
    } catch (error) {
      console.error('Error rejecting owner:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async (ownerId) => {
    if (!actionReason.trim()) {
      setToast({ isOpen: true, title: 'Heads up', message: 'Please provide a reason for suspension', type: 'warning' });
      return;
    }
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.USERS}/admin/owners/${ownerId}/suspend`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: actionReason })
      });

      const data = await response.json();
      if (data.success) {
        fetchOwners();
        setShowActionModal(false);
        setActionReason('');
      }
    } catch (error) {
      console.error('Error suspending owner:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (ownerId) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.USERS}/admin/owners/${ownerId}/reactivate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: actionReason })
      });

      const data = await response.json();
      if (data.success) {
        fetchOwners();
        setShowActionModal(false);
        setActionReason('');
      }
    } catch (error) {
      console.error('Error reactivating owner:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const openActionModal = (owner, type) => {
    setSelectedOwner(owner);
    setActionType(type);
    setActionReason('');
    setShowActionModal(true);
  };

  const executeAction = () => {
    if (!selectedOwner) return;

    switch (actionType) {
      case 'verify':
        handleVerify(selectedOwner._id);
        break;
      case 'reject':
        handleReject(selectedOwner._id);
        break;
      case 'suspend':
        handleSuspend(selectedOwner._id);
        break;
      case 'reactivate':
        handleReactivate(selectedOwner._id);
        break;
      default:
        break;
    }
  };

  const handleVerifyDocument = async (ownerId, documentId, action, notes) => {
    setDocLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Convert action to status: 'verify' -> 'verified', 'reject' -> 'rejected'
      const status = action === 'verify' ? 'verified' : 'rejected';
      const response = await fetch(`${API_ENDPOINTS.USERS}/admin/owners/${ownerId}/documents/${documentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, notes })
      });

      const data = await response.json();
      if (data.success) {
        // Refresh the owner data to show updated documents
        const ownerResponse = await fetch(`${API_ENDPOINTS.USERS}/admin/owners/${ownerId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const ownerData = await ownerResponse.json();
        if (ownerData.success) {
          setSelectedOwner(ownerData.owner);
        }
        setShowDocModal(false);
        setDocNotes('');
        setSelectedDoc(null);
        fetchOwners(); // Refresh list to update any stats
      }
    } catch (error) {
      console.error('Error updating document:', error);
    } finally {
      setDocLoading(false);
    }
  };

  const openDocActionModal = (doc, action) => {
    setSelectedDoc(doc);
    setDocAction(action);
    setDocNotes('');
    setShowDocModal(true);
  };

  const executeDocAction = () => {
    if (!selectedOwner || !selectedDoc) return;
    handleVerifyDocument(selectedOwner._id, selectedDoc._id, docAction, docNotes);
  };

  const getDocStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pending' },
      verified: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Verified' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Rejected' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const getDocTypeLabel = (type) => {
    const labels = {
      aadhar: 'Aadhar Card',
      pan_card: 'PAN Card',
      gst_certificate: 'GST Certificate',
      business_registration: 'Business Registration',
      address_proof: 'Address Proof',
      license: 'License',
      id_proof: 'ID Proof',
      other: 'Other Document'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pending' },
      verified: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Verified' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Rejected' },
      suspended: { bg: 'bg-gray-100', text: 'text-gray-800', icon: AlertCircle, label: 'Suspended' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Owner Verification"
        description="Manage and verify vehicle owner accounts"
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {[
          { key: 'all', label: 'Total', icon: Users, color: 'blue' },
          { key: 'pending', label: 'Pending', icon: Clock, color: 'yellow' },
          { key: 'verified', label: 'Verified', icon: ShieldCheck, color: 'green' },
          { key: 'rejected', label: 'Rejected', icon: ShieldOff, color: 'red' },
          { key: 'suspended', label: 'Suspended', icon: ShieldAlert, color: 'gray' }
        ].map(stat => {
          const Icon = stat.icon;
          const isActive = filters.status === stat.key;
          return (
            <button
              key={stat.key}
              onClick={() => setFilters(prev => ({ ...prev, status: stat.key }))}
              className={`p-4 rounded-xl border-2 transition-all ${
                isActive
                  ? `border-${stat.color}-500 bg-${stat.color}-50`
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <Icon className={`w-5 h-5 text-${stat.color}-600`} />
                <span className="text-2xl font-bold text-gray-900">{stats[stat.key] || 0}</span>
              </div>
              <p className="text-sm text-gray-600 mt-1 text-left">{stat.label}</p>
            </button>
          );
        })}
      </div>

      {/* Search and Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, email, phone, or travels name..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <Button type="submit" variant="primary">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            <RefreshButton onClick={fetchOwners} loading={loading} />
          </form>
        </CardContent>
      </Card>

      {/* Owners List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : owners.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No owners found</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-gray-200">
                {owners.map((owner) => (
                  <div key={owner._id} className="p-4 space-y-3">
                    {/* Owner Info Row */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center min-w-0">
                        <div className="flex-shrink-0 h-10 w-10">
                          {owner.photo?.url ? (
                            <img src={owner.photo.url} alt={owner.name} className="h-10 w-10 rounded-full object-cover" />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-blue-600 font-medium">{owner.name?.charAt(0).toUpperCase()}</span>
                            </div>
                          )}
                        </div>
                        <div className="ml-3 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{owner.name}</div>
                          <div className="text-xs text-gray-500 flex items-center truncate">
                            {owner.email}
                            {owner.isEmailVerified && (
                              <CheckCircle className="w-3 h-3 text-green-500 ml-1 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      </div>
                      {getStatusBadge(owner.verificationStatus)}
                    </div>

                    {/* Details Row */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center text-gray-600">
                        <Building2 className="w-3.5 h-3.5 mr-1.5 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{owner.travelsInfo?.name || 'Not Set'}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Phone className="w-3.5 h-3.5 mr-1.5 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{owner.phone || 'N/A'}</span>
                      </div>
                      {owner.travelsInfo?.address?.city && (
                        <div className="flex items-center text-gray-600">
                          <MapPin className="w-3.5 h-3.5 mr-1.5 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{owner.travelsInfo.address.city}, {owner.travelsInfo.address.state}</span>
                        </div>
                      )}
                      <div className="flex items-center text-gray-600">
                        <FileText className="w-3.5 h-3.5 mr-1.5 text-gray-400 flex-shrink-0" />
                        {owner.documents && owner.documents.length > 0 ? (
                          <span>
                            <span className="text-green-600 font-medium">{owner.documents.filter(d => d.status === 'verified').length}</span>
                            <span className="text-gray-400">/</span>
                            <span>{owner.documents.length}</span> docs
                          </span>
                        ) : (
                          <span className="text-gray-400">No docs</span>
                        )}
                      </div>
                    </div>

                    {/* Business type (admin override — controls the owner's features) */}
                    <div className="flex items-center justify-between text-xs pt-1">
                      <span className="flex items-center text-gray-500">
                        <Building2 className="w-3.5 h-3.5 mr-1.5 text-gray-400" /> Business type
                      </span>
                      {renderBusinessType(owner)}
                    </div>

                    {/* Subscription plan (admin override) */}
                    <div className="flex items-center justify-between text-xs">
                      <span className="flex items-center text-gray-500">
                        <ShieldCheck className="w-3.5 h-3.5 mr-1.5 text-gray-400" /> Plan
                      </span>
                      {renderPlanControl(owner)}
                    </div>

                    {/* Actions Row */}
                    <div className="flex items-center justify-end space-x-3 pt-1 border-t border-gray-100">
                      <button
                        onClick={() => { setSelectedOwner(owner); setShowDetailsModal(true); }}
                        className="flex items-center text-xs text-blue-600 font-medium px-2 py-1 rounded hover:bg-blue-50"
                      >
                        <Eye className="w-4 h-4 mr-1" /> View
                      </button>
                      <button
                        onClick={() => shareOwner(owner)}
                        className={`flex items-center text-xs font-medium px-2 py-1 rounded ${copiedId === owner._id ? 'text-green-600 bg-green-50' : 'text-purple-600 hover:bg-purple-50'}`}
                      >
                        {copiedId === owner._id ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Share2 className="w-4 h-4 mr-1" /> Share</>}
                      </button>
                      {owner.verificationStatus === 'pending' && (
                        <>
                          <button
                            onClick={() => openActionModal(owner, 'verify')}
                            className="flex items-center text-xs text-green-600 font-medium px-2 py-1 rounded hover:bg-green-50"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Verify
                          </button>
                          <button
                            onClick={() => openActionModal(owner, 'reject')}
                            className="flex items-center text-xs text-red-600 font-medium px-2 py-1 rounded hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </button>
                        </>
                      )}
                      {owner.verificationStatus === 'verified' && (
                        <button
                          onClick={() => openActionModal(owner, 'suspend')}
                          className="flex items-center text-xs text-orange-600 font-medium px-2 py-1 rounded hover:bg-orange-50"
                        >
                          <ShieldOff className="w-4 h-4 mr-1" /> Suspend
                        </button>
                      )}
                      {(owner.verificationStatus === 'rejected' || owner.verificationStatus === 'suspended') && (
                        <button
                          onClick={() => openActionModal(owner, 'reactivate')}
                          className="flex items-center text-xs text-green-600 font-medium px-2 py-1 rounded hover:bg-green-50"
                        >
                          <ShieldCheck className="w-4 h-4 mr-1" /> Reactivate
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Travels/Company</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documents</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {owners.map((owner) => (
                      <tr key={owner._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              {owner.photo?.url ? (
                                <img src={owner.photo.url} alt={owner.name} className="h-10 w-10 rounded-full object-cover" />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                  <span className="text-blue-600 font-medium">{owner.name?.charAt(0).toUpperCase()}</span>
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{owner.name}</div>
                              <div className="text-sm text-gray-500 flex items-center">
                                {owner.email}
                                {owner.isEmailVerified && (
                                  <span className="ml-1 inline-flex items-center" title="Email Verified">
                                    <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">{owner.travelsInfo?.name || 'Not Set'}</div>
                              {owner.travelsInfo?.address?.city && (
                                <div className="text-sm text-gray-500 flex items-center">
                                  <MapPin className="w-3 h-3 mr-1" />
                                  {owner.travelsInfo.address.city}, {owner.travelsInfo.address.state}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Phone className="w-4 h-4 text-gray-400 mr-2" />
                            {owner.phone}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {owner.documents && owner.documents.length > 0 ? (
                            <div className="flex items-center space-x-1">
                              <FileText className="w-4 h-4 text-gray-400" />
                              <span className="text-sm">
                                <span className="text-green-600 font-medium">{owner.documents.filter(d => d.status === 'verified').length}</span>
                                <span className="text-gray-400">/</span>
                                <span className="text-gray-600">{owner.documents.length}</span>
                              </span>
                              {owner.documents.some(d => d.status === 'pending') && (
                                <span className="ml-1 w-2 h-2 bg-yellow-400 rounded-full" title="Has pending documents" />
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">None</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {renderBusinessType(owner)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {renderPlanControl(owner)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(owner.verificationStatus)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => { setSelectedOwner(owner); setShowDetailsModal(true); }}
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="View Details"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => shareOwner(owner)}
                              className={`p-1 ${copiedId === owner._id ? 'text-green-600' : 'text-purple-600 hover:text-purple-900'}`}
                              title={copiedId === owner._id ? 'Link copied!' : 'Share public page'}
                            >
                              {copiedId === owner._id ? <Check className="w-5 h-5" /> : <Share2 className="w-5 h-5" />}
                            </button>
                            {owner.verificationStatus === 'pending' && (
                              <>
                                <button onClick={() => openActionModal(owner, 'verify')} className="text-green-600 hover:text-green-900 p-1" title="Verify">
                                  <CheckCircle className="w-5 h-5" />
                                </button>
                                <button onClick={() => openActionModal(owner, 'reject')} className="text-red-600 hover:text-red-900 p-1" title="Reject">
                                  <XCircle className="w-5 h-5" />
                                </button>
                              </>
                            )}
                            {owner.verificationStatus === 'verified' && (
                              <button onClick={() => openActionModal(owner, 'suspend')} className="text-orange-600 hover:text-orange-900 p-1" title="Suspend">
                                <ShieldOff className="w-5 h-5" />
                              </button>
                            )}
                            {(owner.verificationStatus === 'rejected' || owner.verificationStatus === 'suspended') && (
                              <button onClick={() => openActionModal(owner, 'reactivate')} className="text-green-600 hover:text-green-900 p-1" title="Reactivate">
                                <ShieldCheck className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-6 py-4 border-t flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} owners
              </p>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Owner Details Modal */}
      {showDetailsModal && selectedOwner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Owner Details</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Profile Section */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 h-20 w-20">
                  {selectedOwner.photo?.url ? (
                    <img
                      src={selectedOwner.photo.url}
                      alt={selectedOwner.name}
                      className="h-20 w-20 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="h-20 w-20 rounded-xl bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 text-2xl font-bold">
                        {selectedOwner.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900">{selectedOwner.name}</h3>
                  <div className="mt-1 space-y-1">
                    <p className="text-sm text-gray-600 flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      {selectedOwner.email}
                      {selectedOwner.isEmailVerified ? (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Email Verified
                        </span>
                      ) : (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Email Not Verified
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-600 flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      {selectedOwner.phone}
                    </p>
                  </div>
                  <div className="mt-2 flex items-center space-x-2">
                    {getStatusBadge(selectedOwner.verificationStatus)}
                    {selectedOwner.setupComplete && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Setup Complete
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Travels Info */}
              {selectedOwner.travelsInfo && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 flex items-center mb-3">
                    <Building2 className="w-5 h-5 mr-2 text-blue-600" />
                    Travels/Company Details
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Company Name</p>
                      <p className="font-medium">{selectedOwner.travelsInfo.name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Business Phone</p>
                      <p className="font-medium">{selectedOwner.travelsInfo.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">City</p>
                      <p className="font-medium">{selectedOwner.travelsInfo.address?.city || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">State</p>
                      <p className="font-medium">{selectedOwner.travelsInfo.address?.state || 'N/A'}</p>
                    </div>
                    {selectedOwner.travelsInfo.gstNumber && (
                      <div>
                        <p className="text-gray-500">GST Number</p>
                        <p className="font-medium">{selectedOwner.travelsInfo.gstNumber}</p>
                      </div>
                    )}
                    {selectedOwner.travelsInfo.panNumber && (
                      <div>
                        <p className="text-gray-500">PAN Number</p>
                        <p className="font-medium">{selectedOwner.travelsInfo.panNumber}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Documents Section with Verification */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-gray-900 flex items-center mb-3">
                  <FileText className="w-5 h-5 mr-2 text-blue-600" />
                  Verification Documents
                  {selectedOwner.documents && selectedOwner.documents.length > 0 && (
                    <span className="ml-2 text-sm text-gray-500">
                      ({selectedOwner.documents.filter(d => d.status === 'verified').length}/{selectedOwner.documents.length} verified)
                    </span>
                  )}
                </h4>

                {!selectedOwner.documents || selectedOwner.documents.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No documents uploaded yet</p>
                    <p className="text-sm text-gray-400 mt-1">Owner needs to upload verification documents</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedOwner.documents.map((doc) => (
                      <div
                        key={doc._id}
                        className={`p-3 rounded-lg border ${
                          doc.status === 'verified'
                            ? 'bg-green-50 border-green-200'
                            : doc.status === 'rejected'
                            ? 'bg-red-50 border-red-200'
                            : 'bg-yellow-50 border-yellow-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <FileText className="w-8 h-8 text-gray-500" />
                            <div>
                              <p className="font-medium text-gray-900">{getDocTypeLabel(doc.type)}</p>
                              {doc.name && <p className="text-xs text-gray-500">{doc.name}</p>}
                              <p className="text-xs text-gray-400">
                                Uploaded: {formatDate(doc.uploadedAt)}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-3">
                            {getDocStatusBadge(doc.status)}

                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="View Document"
                            >
                              <ExternalLink className="w-5 h-5" />
                            </a>

                            {doc.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => openDocActionModal(doc, 'verify')}
                                  className="text-green-600 hover:text-green-800 p-1"
                                  title="Verify Document"
                                >
                                  <FileCheck className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => openDocActionModal(doc, 'reject')}
                                  className="text-red-600 hover:text-red-800 p-1"
                                  title="Reject Document"
                                >
                                  <FileX className="w-5 h-5" />
                                </button>
                              </>
                            )}

                            {doc.status === 'rejected' && (
                              <button
                                onClick={() => openDocActionModal(doc, 'verify')}
                                className="text-green-600 hover:text-green-800 p-1"
                                title="Re-verify Document"
                              >
                                <FileCheck className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        </div>

                        {doc.notes && (
                          <div className={`mt-2 p-2 rounded text-sm ${
                            doc.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'
                          }`}>
                            <strong>Notes:</strong> {doc.notes}
                          </div>
                        )}

                        {doc.verifiedAt && (
                          <p className="text-xs text-gray-500 mt-1">
                            {doc.status === 'verified' ? 'Verified' : 'Reviewed'} on {formatDate(doc.verifiedAt)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Rejection Reason */}
              {selectedOwner.rejectionReason && (
                <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <h4 className="font-medium text-red-800 mb-2">
                    {selectedOwner.verificationStatus === 'suspended' ? 'Suspension Reason' : 'Rejection Reason'}
                  </h4>
                  <p className="text-sm text-red-700">{selectedOwner.rejectionReason}</p>
                </div>
              )}

              {/* Registration Date */}
              <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t">
                <span>Registered on: {formatDate(selectedOwner.createdAt)}</span>
                {selectedOwner.verifiedAt && (
                  <span>Verified on: {formatDate(selectedOwner.verifiedAt)}</span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                Close
              </Button>

              {selectedOwner.verificationStatus === 'pending' && (
                <>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setShowDetailsModal(false);
                      openActionModal(selectedOwner, 'reject');
                    }}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    variant="success"
                    onClick={() => {
                      setShowDetailsModal(false);
                      openActionModal(selectedOwner, 'verify');
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Verify
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Confirmation Modal */}
      {showActionModal && selectedOwner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                {actionType === 'verify' && 'Verify Owner'}
                {actionType === 'reject' && 'Reject Owner'}
                {actionType === 'suspend' && 'Suspend Owner'}
                {actionType === 'reactivate' && 'Reactivate Owner'}
              </h2>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-4">
                {actionType === 'verify' && `Are you sure you want to verify "${selectedOwner.name}"? They will be able to add vehicles after verification.`}
                {actionType === 'reject' && `Are you sure you want to reject "${selectedOwner.name}"? Please provide a reason.`}
                {actionType === 'suspend' && `Are you sure you want to suspend "${selectedOwner.name}"? They will no longer be able to add or manage vehicles.`}
                {actionType === 'reactivate' && `Are you sure you want to reactivate "${selectedOwner.name}"? Their account will be verified again.`}
              </p>

              {(actionType === 'reject' || actionType === 'suspend') && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder={`Enter reason for ${actionType}...`}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              )}

              {(actionType === 'verify' || actionType === 'reactivate') && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder="Add any notes..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowActionModal(false);
                  setActionReason('');
                }}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                variant={actionType === 'verify' || actionType === 'reactivate' ? 'success' : 'danger'}
                onClick={executeAction}
                disabled={actionLoading || ((actionType === 'reject' || actionType === 'suspend') && !actionReason.trim())}
              >
                {actionLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <>
                    {actionType === 'verify' && <CheckCircle className="w-4 h-4 mr-2" />}
                    {actionType === 'reject' && <XCircle className="w-4 h-4 mr-2" />}
                    {actionType === 'suspend' && <ShieldOff className="w-4 h-4 mr-2" />}
                    {actionType === 'reactivate' && <ShieldCheck className="w-4 h-4 mr-2" />}
                  </>
                )}
                {actionType.charAt(0).toUpperCase() + actionType.slice(1)}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Document Verification Modal */}
      {showDocModal && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                {docAction === 'verify' ? 'Verify Document' : 'Reject Document'}
              </h2>
            </div>

            <div className="p-6">
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Document Type</p>
                <p className="font-medium text-gray-900">{getDocTypeLabel(selectedDoc.type)}</p>
                {selectedDoc.name && (
                  <>
                    <p className="text-sm text-gray-600 mt-2">File Name</p>
                    <p className="font-medium text-gray-900">{selectedDoc.name}</p>
                  </>
                )}
                <a
                  href={selectedDoc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm mt-2"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  View Document
                </a>
              </div>

              <p className="text-gray-600 mb-4">
                {docAction === 'verify'
                  ? 'Are you sure this document is valid and authentic?'
                  : 'Please provide a reason for rejecting this document.'}
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes {docAction === 'reject' && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  value={docNotes}
                  onChange={(e) => setDocNotes(e.target.value)}
                  placeholder={docAction === 'verify' ? 'Add verification notes (optional)...' : 'Reason for rejection...'}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required={docAction === 'reject'}
                />
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDocModal(false);
                  setDocNotes('');
                  setSelectedDoc(null);
                }}
                disabled={docLoading}
              >
                Cancel
              </Button>
              <Button
                variant={docAction === 'verify' ? 'success' : 'danger'}
                onClick={executeDocAction}
                disabled={docLoading || (docAction === 'reject' && !docNotes.trim())}
              >
                {docLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : docAction === 'verify' ? (
                  <FileCheck className="w-4 h-4 mr-2" />
                ) : (
                  <FileX className="w-4 h-4 mr-2" />
                )}
                {docAction === 'verify' ? 'Verify Document' : 'Reject Document'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Toast isOpen={toast.isOpen} onClose={() => setToast(p => ({ ...p, isOpen: false }))} title={toast.title} message={toast.message} type={toast.type} />

      <ConfirmModal
        isOpen={!!pendingBiz}
        onClose={() => setPendingBiz(null)}
        onConfirm={() => { const p = pendingBiz; setPendingBiz(null); if (p) handleSetBusinessType(p.ownerId, p.businessType); }}
        type="warning"
        title="Change business type?"
        message={pendingBiz ? `Set ${pendingBiz.ownerName || 'this owner'}'s business type to "${{ rental: 'Renting', sale: 'Selling', both: 'Both' }[pendingBiz.businessType]}"? This changes which features and menus they see.` : ''}
        confirmText="Yes, change it"
        cancelText="Cancel"
      />

      <ConfirmModal
        isOpen={!!pendingPlan}
        onClose={() => setPendingPlan(null)}
        onConfirm={() => { const p = pendingPlan; setPendingPlan(null); if (p) handleSetPlan(p.ownerId, p.plan, p.expiresAt, p.vehicleLimit); }}
        type="info"
        title="Change subscription plan?"
        message={pendingPlan
          ? `Set ${pendingPlan.ownerName || 'this owner'}'s plan to "${pendingPlan.plan}"? `
            + (pendingPlan.plan === 'Free'
              ? 'They will be limited to the Free plan (3 vehicles) straight away.'
              : `They get ${pendingPlan.vehicleLimit !== '' ? pendingPlan.vehicleLimit : PLAN_DEFAULT_VEHICLES[pendingPlan.plan]} vehicles, `
                + (pendingPlan.expiresAt
                  ? `effective immediately and expiring on ${fmtDate(pendingPlan.expiresAt)}, after which they drop back to Free limits.`
                  : 'effective immediately, and it never expires.'))
          : ''}
        confirmText="Yes, set plan"
        cancelText="Cancel"
      />
    </div>
  );
};

export default OwnersList;
